/**
 * 音频质量实时监控系统
 * 
 * 功能：
 * - 在用户第一次播放时实时检测音频质量
 * - 检测卡顿、断音、波形异常等问题
 * - 自动禁用 TUAC 并提示用户
 * 
 * 检测特征：
 * 1. 静音段检测 (Silence Detection) - 检测音频中断
 * 2. 波形跳变检测 (Discontinuity Detection) - 检测样本突变
 * 3. 频域异常检测 (Spectral Anomaly Detection) - 检测频谱异常
 * 4. RMS 波动检测 (RMS Fluctuation Detection) - 检测能量异常波动
 */

import { showHintPlus } from '../../utils/uiHint';

// 检测配置
const CONFIG = {
  // 检测时长（秒）
  MONITOR_DURATION: 30,
  
  // 分析参数
  FFT_SIZE: 2048,
  SMOOTHING_TIME_CONSTANT: 0.3,
  
  // 异常阈值
  THRESHOLDS: {
    // 静音检测：连续多少帧 RMS < 阈值认为是静音
    SILENCE_FRAMES: 5,
    SILENCE_RMS: 0.001,
    
    // 跳变检测：相邻样本差值超过阈值
    DISCONTINUITY_THRESHOLD: 0.5,
    
    // RMS 波动：短时间内 RMS 剧烈变化
    RMS_FLUCTUATION_RATIO: 10.0,
    
    // 频谱异常：高频能量异常
    SPECTRAL_ANOMALY_RATIO: 15.0,
  },
  
  // 判定规则：多少次异常事件后判定为质量问题
  ANOMALY_LIMITS: {
    SILENCE: 3,          // 3 次静音事件
    DISCONTINUITY: 5,    // 5 次跳变
    RMS_SPIKE: 4,        // 4 次 RMS 突变
    SPECTRAL: 3,         // 3 次频谱异常
  }
};

export default class AudioQualityMonitor {
  constructor(audioContext) {
    this.ctx = audioContext;
    this.analyserNode = null;
    this.scriptProcessorNode = null;
    
    // 监控状态
    this.isMonitoring = false;
    this.monitorStartTime = 0;
    this.hasCompletedMonitoring = false;
    
    // 检测计数器
    this.anomalyCounts = {
      silence: 0,
      discontinuity: 0,
      rmsSpike: 0,
      spectral: 0,
    };
    
    // 历史数据
    this.lastRMS = 0;
    this.lastTimeDomainData = null;
    this.silenceFrameCount = 0;
    
    // 回调
    this.onQualityIssueDetected = null;
  }
  
  /**
   * 开始监控（插入到音频链路中）
   */
  start(sourceNode, destinationNode, onQualityIssue) {
    if (this.isMonitoring) return;
    if (this.hasCompletedMonitoring) return;
    
    try {
      this.onQualityIssueDetected = onQualityIssue;
      
      // 创建分析节点
      this.analyserNode = this.ctx.createAnalyser();
      this.analyserNode.fftSize = CONFIG.FFT_SIZE;
      this.analyserNode.smoothingTimeConstant = CONFIG.SMOOTHING_TIME_CONSTANT;
      
      // 创建 ScriptProcessor 用于实时分析
      const bufferSize = 4096;
      this.scriptProcessorNode = this.ctx.createScriptProcessor(bufferSize, 2, 2);
      
      // 连接音频链路: source -> analyser -> scriptProcessor -> destination
      sourceNode.connect(this.analyserNode);
      this.analyserNode.connect(this.scriptProcessorNode);
      this.scriptProcessorNode.connect(destinationNode);
      
      // 处理音频数据
      this.scriptProcessorNode.onaudioprocess = (event) => {
        this._processAudioFrame(event);
      };
      
      this.isMonitoring = true;
      this.monitorStartTime = Date.now();
      
      console.log('[AudioQualityMonitor] 开始监控音频质量，时长:', CONFIG.MONITOR_DURATION, '秒');
    } catch (err) {
      console.error('[AudioQualityMonitor] 启动失败:', err);
    }
  }
  
  /**
   * 停止监控
   */
  stop() {
    if (!this.isMonitoring) return;
    
    try {
      if (this.scriptProcessorNode) {
        this.scriptProcessorNode.onaudioprocess = null;
        this.scriptProcessorNode.disconnect();
        this.scriptProcessorNode = null;
      }
      
      if (this.analyserNode) {
        this.analyserNode.disconnect();
        this.analyserNode = null;
      }
      
      this.isMonitoring = false;
      this.hasCompletedMonitoring = true;
      
      console.log('[AudioQualityMonitor] 监控完成，异常统计:', this.anomalyCounts);
    } catch (err) {
      console.error('[AudioQualityMonitor] 停止失败:', err);
    }
  }
  
  /**
   * 处理音频帧
   */
  _processAudioFrame(event) {
    // 检查是否超时
    const elapsed = (Date.now() - this.monitorStartTime) / 1000;
    if (elapsed >= CONFIG.MONITOR_DURATION) {
      this.stop();
      console.log('[AudioQualityMonitor] 监控完成 - 未检测到质量问题 ✓');
      return;
    }
    
    // 获取时域数据（声明在外部，以便后续使用）
    let channelDataL = null;
    let channelDataR = null;
    
    try {
      // 获取时域数据
      const bufferSize = event.inputBuffer.length;
      channelDataL = event.inputBuffer.getChannelData(0);
      channelDataR = event.inputBuffer.numberOfChannels > 1 
        ? event.inputBuffer.getChannelData(1) 
        : channelDataL;
      
      // 获取频域数据
      const frequencyData = new Uint8Array(this.analyserNode.frequencyBinCount);
      this.analyserNode.getByteFrequencyData(frequencyData);
      
      // 1. RMS 计算
      const rms = this._calculateRMS(channelDataL, channelDataR);
      
      // 2. 静音检测
      if (this._detectSilence(rms)) {
        this.anomalyCounts.silence++;
        console.warn('[AudioQualityMonitor] 检测到静音异常，计数:', this.anomalyCounts.silence);
        this._checkAndReport('silence');
      }
      
      // 3. 波形跳变检测
      if (this._detectDiscontinuity(channelDataL, channelDataR)) {
        this.anomalyCounts.discontinuity++;
        console.warn('[AudioQualityMonitor] 检测到波形跳变，计数:', this.anomalyCounts.discontinuity);
        this._checkAndReport('discontinuity');
      }
      
      // 4. RMS 突变检测
      if (this._detectRMSSpike(rms)) {
        this.anomalyCounts.rmsSpike++;
        console.warn('[AudioQualityMonitor] 检测到 RMS 突变，计数:', this.anomalyCounts.rmsSpike);
        this._checkAndReport('rmsSpike');
      }
      
      // 5. 频谱异常检测
      if (this._detectSpectralAnomaly(frequencyData)) {
        this.anomalyCounts.spectral++;
        console.warn('[AudioQualityMonitor] 检测到频谱异常，计数:', this.anomalyCounts.spectral);
        this._checkAndReport('spectral');
      }
      
      // 更新历史
      this.lastRMS = rms;
      this.lastTimeDomainData = channelDataL;
      
    } catch (err) {
      console.error('[AudioQualityMonitor] 处理失败:', err);
    }
    
    // 透传音频（不修改）
    if (channelDataL && channelDataR) {
      const outputL = event.outputBuffer.getChannelData(0);
      const outputR = event.outputBuffer.getChannelData(1);
      outputL.set(channelDataL);
      outputR.set(channelDataR);
    }
  }
  
  /**
   * 计算 RMS（均方根）
   */
  _calculateRMS(channelL, channelR) {
    let sumL = 0;
    let sumR = 0;
    const len = channelL.length;
    
    for (let i = 0; i < len; i++) {
      sumL += channelL[i] * channelL[i];
      sumR += channelR[i] * channelR[i];
    }
    
    return Math.sqrt((sumL + sumR) / (len * 2));
  }
  
  /**
   * 检测静音段
   */
  _detectSilence(rms) {
    if (rms < CONFIG.THRESHOLDS.SILENCE_RMS) {
      this.silenceFrameCount++;
      if (this.silenceFrameCount >= CONFIG.THRESHOLDS.SILENCE_FRAMES) {
        this.silenceFrameCount = 0; // 重置，避免重复计数
        return true;
      }
    } else {
      this.silenceFrameCount = 0;
    }
    return false;
  }
  
  /**
   * 检测波形跳变（相邻样本突变）
   */
  _detectDiscontinuity(channelL, channelR) {
    const threshold = CONFIG.THRESHOLDS.DISCONTINUITY_THRESHOLD;
    
    for (let i = 1; i < channelL.length; i++) {
      const diffL = Math.abs(channelL[i] - channelL[i - 1]);
      const diffR = Math.abs(channelR[i] - channelR[i - 1]);
      
      if (diffL > threshold || diffR > threshold) {
        return true;
      }
    }
    
    return false;
  }
  
  /**
   * 检测 RMS 突变
   */
  _detectRMSSpike(currentRMS) {
    if (this.lastRMS === 0) return false;
    
    const ratio = Math.max(currentRMS, 0.0001) / Math.max(this.lastRMS, 0.0001);
    
    if (ratio > CONFIG.THRESHOLDS.RMS_FLUCTUATION_RATIO || 
        ratio < 1 / CONFIG.THRESHOLDS.RMS_FLUCTUATION_RATIO) {
      return true;
    }
    
    return false;
  }
  
  /**
   * 检测频谱异常（高频能量异常高）
   */
  _detectSpectralAnomaly(frequencyData) {
    const binCount = frequencyData.length;
    const midPoint = Math.floor(binCount / 2);
    
    // 计算低频和高频能量
    let lowFreqEnergy = 0;
    let highFreqEnergy = 0;
    
    for (let i = 0; i < midPoint; i++) {
      lowFreqEnergy += frequencyData[i];
    }
    
    for (let i = midPoint; i < binCount; i++) {
      highFreqEnergy += frequencyData[i];
    }
    
    // 如果高频能量异常高（可能是数字噪声）
    if (lowFreqEnergy > 0) {
      const ratio = highFreqEnergy / lowFreqEnergy;
      if (ratio > CONFIG.THRESHOLDS.SPECTRAL_ANOMALY_RATIO) {
        return true;
      }
    }
    
    return false;
  }
  
  /**
   * 检查异常计数并报告
   */
  _checkAndReport(type) {
    const limits = CONFIG.ANOMALY_LIMITS;
    const counts = this.anomalyCounts;
    
    // 检查是否超过阈值
    const shouldReport = 
      (type === 'silence' && counts.silence >= limits.SILENCE) ||
      (type === 'discontinuity' && counts.discontinuity >= limits.DISCONTINUITY) ||
      (type === 'rmsSpike' && counts.rmsSpike >= limits.RMS_SPIKE) ||
      (type === 'spectral' && counts.spectral >= limits.SPECTRAL);
    
    if (shouldReport) {
      this._reportQualityIssue(type);
    }
  }
  
  /**
   * 报告质量问题
   */
  _reportQualityIssue(type) {
    console.error('[AudioQualityMonitor] ❌ 检测到音频质量问题:', type, '异常统计:', this.anomalyCounts);
    
    // 停止监控
    this.stop();
    
    // 回调通知
    if (typeof this.onQualityIssueDetected === 'function') {
      this.onQualityIssueDetected({
        type,
        counts: { ...this.anomalyCounts },
        timestamp: Date.now(),
      });
    }
    
    // 用户提示
    this._showUserNotification(type);
  }
  
  /**
   * 显示用户通知
   */
  _showUserNotification(type) {
    const messages = {
      silence: '检测到音频卡顿，已自动禁用音效增强',
      discontinuity: '检测到音频波形异常，已自动禁用音效增强',
      rmsSpike: '检测到音频能量异常波动，已自动禁用音效增强',
      spectral: '检测到音频频谱异常，已自动禁用音效增强',
    };
    
    const message = messages[type] || '检测到音频质量问题，已自动禁用音效增强';
    
    showHintPlus(message, { duration: 6000 });
  }
  
  /**
   * 重置监控器（用于下一首歌）
   */
  reset() {
    this.hasCompletedMonitoring = false;
    this.anomalyCounts = {
      silence: 0,
      discontinuity: 0,
      rmsSpike: 0,
      spectral: 0,
    };
    this.lastRMS = 0;
    this.lastTimeDomainData = null;
    this.silenceFrameCount = 0;
  }
}
